import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const gradeColor = (g) => g === "A" ? "text-emerald-400" : g === "B" ? "text-lime-400" : g === "C" ? "text-amber-400" : g === "D" ? "text-orange-400" : g === "E" ? "text-red-400" : "text-slate-500";

function fmtDays(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const days = Math.floor((Date.now() - d.getTime()) / 86400e3);
    return `il y a ${days}j`;
  } catch { return iso; }
}

export default function StaffClients() {
  const [rows, setRows] = useState([]);
  const [orderBy, setOrderBy] = useState("health");
  const [orderDir, setOrderDir] = useState("asc");
  const [planF, setPlanF] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      p.set("order_by", orderBy); p.set("order_dir", orderDir);
      p.set("limit", "100");
      if (planF !== "all") p.set("plan", planF);
      if (statusF !== "all") p.set("subscription_status", statusF);
      const r = await api.get(`/staff/clients?${p.toString()}`);
      setRows(r.data.clients || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [orderBy, orderDir, planF, statusF]);

  const openDetail = async (id) => {
    try {
      const [d, h] = await Promise.all([
        api.get(`/staff/clients/${id}`),
        api.get(`/staff/clients/${id}/health-timeseries?weeks=12`),
      ]);
      setDetail({ ...d.data, health_ts: h.data.weeks });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="staff-clients">
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <label className="text-xs text-slate-400 block">Plan</label>
          <Select value={planF} onValueChange={setPlanF}>
            <SelectTrigger className="w-40 bg-slate-950 border-slate-700 text-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tous</SelectItem>
              {["essential","business","scale","enterprise","cabinet"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-slate-400 block">Statut</label>
          <Select value={statusF} onValueChange={setStatusF}>
            <SelectTrigger className="w-40 bg-slate-950 border-slate-700 text-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tous</SelectItem>
              {["active","trialing","past_due","canceled","unpaid"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-slate-400 block">Tri</label>
          <Select value={orderBy} onValueChange={setOrderBy}>
            <SelectTrigger className="w-40 bg-slate-950 border-slate-700 text-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Nom</SelectItem>
              <SelectItem value="plan">Plan</SelectItem>
              <SelectItem value="health">Health Score</SelectItem>
              <SelectItem value="last_login">Dernière connexion</SelectItem>
              <SelectItem value="subscription_status">Statut abo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" variant="outline" onClick={() => setOrderDir(orderDir === "asc" ? "desc" : "asc")}
          className="border-slate-700 text-slate-200 bg-transparent hover:bg-slate-800" data-testid="order-dir-toggle">
          {orderDir === "asc" ? "↑ Asc" : "↓ Desc"}
        </Button>
      </div>
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-0">
          {loading ? <p className="text-slate-400 p-6">Chargement…</p> : (
            <Table>
              <TableHeader><TableRow className="border-slate-800">
                <TableHead className="text-slate-400">Client</TableHead>
                <TableHead className="text-slate-400">Plan</TableHead>
                <TableHead className="text-slate-400">Statut</TableHead>
                <TableHead className="text-slate-400">Health</TableHead>
                <TableHead className="text-slate-400">Dernière connexion</TableHead>
                <TableHead className="text-slate-400">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id} className="border-slate-800 hover:bg-slate-800/40" data-testid={`staff-client-${r.id}`}>
                    <TableCell className="font-medium text-slate-200">{r.name}</TableCell>
                    <TableCell><Badge variant="outline" className="uppercase text-[10px]">{r.plan_code}</Badge></TableCell>
                    <TableCell className="text-xs text-slate-400">{r.subscription_status || "—"}</TableCell>
                    <TableCell>
                      <span className={`font-mono font-bold ${gradeColor(r.health_grade)}`}>
                        {r.health_score ?? "—"} {r.health_grade && `· ${r.health_grade}`}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-slate-400">{fmtDays(r.last_login_at)}</TableCell>
                    <TableCell><Button size="sm" variant="outline" onClick={() => openDetail(r.id)}
                      className="border-slate-700 text-slate-200 bg-transparent hover:bg-slate-800"
                      data-testid={`view-detail-${r.id}`}>Voir</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-5xl bg-slate-900 border-slate-800 text-slate-100 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle data-testid="detail-title">{detail?.tenant?.name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3 text-sm">
                <div><div className="text-xs text-slate-500">Plan</div><div className="uppercase">{detail.tenant.plan_code}</div></div>
                <div><div className="text-xs text-slate-500">Statut</div><div>{detail.tenant.stripe_subscription_status || "—"}</div></div>
                <div><div className="text-xs text-slate-500">Health</div><div className={gradeColor(detail.health_score?.grade)}>{detail.health_score?.score} · {detail.health_score?.grade}</div></div>
                <div><div className="text-xs text-slate-500">Total facturé</div><div className="text-emerald-400">{(detail.invoices_total_paid_eur || 0).toFixed(2)}€</div></div>
              </div>
              {detail.health_ts && detail.health_ts.length > 0 && (
                <Card className="bg-slate-950 border-slate-800">
                  <CardContent className="p-3 h-40" data-testid="health-timeseries-chart">
                    <p className="text-xs text-slate-400 mb-1">Health Score — 12 dernières semaines</p>
                    <ResponsiveContainer>
                      <LineChart data={detail.health_ts}>
                        <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                        <XAxis dataKey="week_key" stroke="#64748b" fontSize={10} />
                        <YAxis stroke="#64748b" fontSize={10} domain={[0, 100]} />
                        <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155" }} />
                        <Line type="monotone" dataKey="score" stroke="#3b82f6" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
              <div>
                <p className="text-xs text-slate-400 mb-1">Factures ({detail.invoices_count_total})</p>
                <div className="max-h-48 overflow-y-auto border border-slate-800 rounded">
                  <Table><TableHeader><TableRow className="border-slate-800">
                    <TableHead className="text-slate-500 text-[11px]">Numéro</TableHead>
                    <TableHead className="text-slate-500 text-[11px]">Statut</TableHead>
                    <TableHead className="text-slate-500 text-[11px]">Payé</TableHead>
                    <TableHead className="text-slate-500 text-[11px]">Date</TableHead>
                    <TableHead className="text-slate-500 text-[11px]">PDF</TableHead>
                  </TableRow></TableHeader><TableBody>
                    {detail.invoices.slice(0, 20).map(i => (
                      <TableRow key={i.id} className="border-slate-800 hover:bg-slate-800/40">
                        <TableCell className="font-mono text-[11px]">{i.number || i.id.slice(0, 12)}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[10px]">{i.event_status}</Badge></TableCell>
                        <TableCell className="text-emerald-300">{i.amount_paid_eur.toFixed(2)}€</TableCell>
                        <TableCell className="text-xs text-slate-500">{i.created ? new Date(i.created * 1000).toLocaleDateString("fr-FR") : "—"}</TableCell>
                        <TableCell>{i.invoice_pdf && <a href={i.invoice_pdf} target="_blank" rel="noreferrer" className="text-blue-400 text-xs hover:underline">PDF</a>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody></Table>
                </div>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-1">Modules — dernière action</p>
                <div className="grid grid-cols-4 gap-2 text-xs">
                  {Object.entries(detail.modules_last_action || {}).map(([m, v]) => (
                    <div key={m} className="border border-slate-800 rounded p-2">
                      <div className="text-slate-500 uppercase text-[10px]">{m}</div>
                      <div className="text-slate-300">{fmtDays(v)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
